/**
 * The question bank's folders and rows, held once.
 *
 * The library page used to keep both in its own state and pass them to the side
 * navigation through a bridge, while the editor, the import screens and the
 * hub read the seed lists directly. A folder renamed in the library never
 * reached the editor's location picker, and a row moved there did not move
 * anywhere else. Everything reads these two stores now.
 */

import * as React from "react"

import { categoryIdsFromTopic } from "@/lib/blueprint/mapping"
import { useProgramKey } from "@/hooks/use-program"
import { LIBRARY_FAVORITES_FOLDER_ID } from "@/lib/library-nav"
import { LIBRARY_FOLDERS, type LibraryItem } from "@/lib/mock/library"
import { programOfCourseFolderId, type ProgramKey } from "@/lib/mock/program-courses"
import { bankAdditionsStore, bankLibraryItems } from "@/lib/question-bank/bank-store"
import type { LibraryFolder } from "@/lib/mock/library-folders"
import { libraryItemsWithBankOverlays } from "@/lib/library-bank-row-overlays"
import { createMemoryStore, createPersistedStore, usePersistedStore } from "@/lib/persisted-store"

export const LIBRARY_FOLDERS_STORE_KEY = "shell:library-folders:v1"

export const libraryFoldersStore = createPersistedStore<LibraryFolder[]>(
  LIBRARY_FOLDERS_STORE_KEY,
  () => LIBRARY_FOLDERS.map((folder) => ({ ...folder })),
)

export const libraryItemsStore = createMemoryStore<LibraryItem[]>(() =>
  libraryItemsWithBankOverlays(bankLibraryItems()),
)

// A question filed into the bank from anywhere shows up in the library at once.
bankAdditionsStore.subscribe(() => {
  libraryItemsStore.set((current) => {
    const have = new Set(current.map((item) => item.id))
    const missing = bankLibraryItems().filter((item) => !have.has(item.id))
    return missing.length === 0 ? current : [...current, ...libraryItemsWithBankOverlays(missing)]
  })
})

/**
 * The folders of one program. Favorites is everyone's; every other folder sits
 * under a course folder, and a course belongs to one program.
 */
function foldersOfProgram(all: LibraryFolder[], program: ProgramKey): LibraryFolder[] {
  const byId = new Map(all.map((folder) => [folder.id, folder]))
  const rootOf = (folder: LibraryFolder): LibraryFolder => {
    let current = folder
    for (let i = 0; i < 12 && current.parentId; i++) {
      const parent = byId.get(current.parentId)
      if (!parent) break
      current = parent
    }
    return current
  }
  return all.filter((folder) => {
    const root = rootOf(folder)
    return root.id === LIBRARY_FAVORITES_FOLDER_ID || programOfCourseFolderId(root.id) === program
  })
}

/** The bank's folders for the program picked at the top. Writes keep the other program's folders. */
export function useLibraryFolders() {
  const [all, setAll] = usePersistedStore(libraryFoldersStore)
  const program = useProgramKey()
  const visible = React.useMemo(() => foldersOfProgram(all, program), [all, program])
  const set = React.useCallback<React.Dispatch<React.SetStateAction<LibraryFolder[]>>>(
    (next) =>
      setAll((current) => {
        const mine = foldersOfProgram(current, program)
        const updated = typeof next === "function" ? next(mine) : next
        const mineIds = new Set(mine.map((folder) => folder.id))
        return [...current.filter((folder) => !mineIds.has(folder.id)), ...updated]
      }),
    [program, setAll],
  )
  return [visible, set] as const
}

/** The questions filed under one course's folder or any folder inside it. */
function itemsOfCourse(items: LibraryItem[], folders: LibraryFolder[], courseCode: string | undefined): LibraryItem[] {
  if (!courseCode) return items
  const root = folders.find((folder) => folder.parentId === null && folder.id === `course-${courseCode.toLowerCase()}`)
  if (!root) return []
  const inside = new Set([root.id])
  for (let changed = true; changed; ) {
    changed = false
    for (const folder of folders) {
      if (folder.parentId && inside.has(folder.parentId) && !inside.has(folder.id)) {
        inside.add(folder.id)
        changed = true
      }
    }
  }
  return items.filter((item) => [item.folderId, ...(item.folderIds ?? [])].some((id) => id && inside.has(id)))
}

/**
 * The questions of the active program. A question belongs to Physical Therapy
 * only when it is filed under a PT course folder; everything else (filed under
 * a nursing course, on the Favorites shelf, or nowhere) is the nursing bank's.
 */
export function useLibraryItems() {
  const [all, setAll] = usePersistedStore(libraryItemsStore)
  const [allFolders] = usePersistedStore(libraryFoldersStore)
  const program = useProgramKey()
  const inProgram = React.useMemo(() => {
    const ptFolders = new Set(foldersOfProgram(allFolders, "pt").map((folder) => folder.id))
    ptFolders.delete(LIBRARY_FAVORITES_FOLDER_ID)
    return (item: LibraryItem) => {
      const inPt = [item.folderId, ...(item.folderIds ?? [])].some((id) => id && ptFolders.has(id))
      return program === "pt" ? inPt : !inPt
    }
  }, [allFolders, program])
  const visible = React.useMemo(() => all.filter(inProgram), [all, inProgram])
  const set = React.useCallback<React.Dispatch<React.SetStateAction<LibraryItem[]>>>(
    (next) =>
      setAll((current) => {
        const mine = current.filter(inProgram)
        const updated = typeof next === "function" ? next(mine) : next
        const mineIds = new Set(mine.map((item) => item.id))
        return [...current.filter((item) => !mineIds.has(item.id)), ...updated]
      }),
    [inProgram, setAll],
  )
  return [visible, set] as const
}

/** The categories a bank question counts toward: its own tags, else the ones its topic implies. */
export function bankItemCategoryIds(item: LibraryItem): string[] {
  if (item.categoryUnassigned) return []
  return item.categoryIds?.length ? item.categoryIds : categoryIdsFromTopic(item.topic)
}

/**
 * Every bank question with the categories it is tagged with, for reports and coverage.
 * Given a course, only the questions filed under that course's bank folder.
 */
export function useBankCategoryRows(course?: { code: string }): Array<{ categoryIds: string[] }> {
  const [items] = useLibraryItems()
  const [folders] = useLibraryFolders()
  return React.useMemo(
    () =>
      itemsOfCourse(items, folders, course?.code).map((item) => ({ categoryIds: bankItemCategoryIds(item) })),
    [items],
  )
}
